import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";
import { scoreStaged } from "../../../openTaskScoring";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function buildData({ faceHeight, lateralFaceArea }) {
  const baseEdge = (2 * lateralFaceArea) / faceHeight;
  const halfBase = baseEdge / 2;
  const lateralEdge = Math.sqrt(halfBase ** 2 + faceHeight ** 2);
  const edgeSum = 4 * baseEdge + 4 * lateralEdge;

  return {
    faceHeight,
    lateralFaceArea,
    baseEdge,
    halfBase,
    lateralEdge,
    edgeSum,
    maxPoints: 3,
  };
}

function createOriginalTask() {
  return {
    type: "E8SquarePyramidEdgesOpenTask",
    data: buildData({ faceHeight: 12, lateralFaceArea: 108 }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 21,
    },
    tags: ["e8", "stereometria", "ostroslupy", "krawedzie", "zadanie-otwarte"],
  };
}

const PYTHAGOREAN_CASES = [
  { halfBase: 6, faceHeight: 8, lateralEdge: 10 },
  { halfBase: 9, faceHeight: 12, lateralEdge: 15 },
  { halfBase: 10, faceHeight: 24, lateralEdge: 26 },
  { halfBase: 12, faceHeight: 16, lateralEdge: 20 },
  { halfBase: 15, faceHeight: 20, lateralEdge: 25 },
];

function createRandomData() {
  const item = pick(PYTHAGOREAN_CASES);
  const baseEdge = 2 * item.halfBase;
  return buildData({
    faceHeight: item.faceHeight,
    lateralFaceArea: (baseEdge * item.faceHeight) / 2,
  });
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8SquarePyramidEdgesOpenTask",
    data: createRandomData(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_21" },
    tags: ["e8", "stereometria", "ostroslupy", "krawedzie", "zadanie-otwarte"],
  };
}

function getRaw(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  if (typeof value.value === "string") return value.value;
  if (typeof value.parsed === "number") return String(value.parsed);
  return "";
}

function parseNum(value) {
  const raw = getRaw(value).trim().replace(",", ".");
  if (!raw) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function isNumCorrect(value, expected) {
  const parsed = parseNum(value);
  return parsed != null && Math.abs(parsed - expected) < 0.001;
}

function NumberInputRow({ label, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ opacity: 0.9 }}>{label}</span>
      <FractionInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label=""
        suffixLatex={"\\text{ cm}"}
        placeholder="np. 18"
      />
    </div>
  );
}

function PyramidSketch({ faceHeight }) {
  const line = "currentColor";
  const accent = "#f0ad3d";
  const fill = "rgba(240,173,61,0.14)";
  const pts = {
    A: { x: 82, y: 165 },
    B: { x: 238, y: 165 },
    C: { x: 284, y: 126 },
    D: { x: 128, y: 126 },
    S: { x: 178, y: 42 },
    M: { x: 160, y: 165 },
  };
  const heightAngle =
    (Math.atan2(pts.M.y - pts.S.y, pts.M.x - pts.S.x) * 180) / Math.PI;
  // przesuniecie etykiety prostopadle do odcinka S-M (w lewo), zeby nie lezala na linii
  const hdx = pts.M.x - pts.S.x;
  const hdy = pts.M.y - pts.S.y;
  const hlen = Math.hypot(hdx, hdy) || 1;
  const perpOffset = 10; // tuz nad linia (z boku), nie na niej
  const along = 0.5; // srodek odcinka S-M
  const heightLabel = {
    x: pts.S.x + hdx * along + (-hdy / hlen) * perpOffset,
    y: pts.S.y + hdy * along + (hdx / hlen) * perpOffset,
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "12px 0 16px" }}>
      <svg width="340" height="205" viewBox="0 0 340 205" role="img" aria-label="Ostrosłup prawidłowy czworokątny">
        <polygon points={`${pts.A.x},${pts.A.y} ${pts.B.x},${pts.B.y} ${pts.C.x},${pts.C.y} ${pts.D.x},${pts.D.y}`} fill="none" stroke={line} strokeWidth="1.8" />
        <polygon points={`${pts.S.x},${pts.S.y} ${pts.A.x},${pts.A.y} ${pts.B.x},${pts.B.y}`} fill={fill} stroke="none" />
        <line x1={pts.S.x} y1={pts.S.y} x2={pts.A.x} y2={pts.A.y} stroke={line} strokeWidth="1.8" />
        <line x1={pts.S.x} y1={pts.S.y} x2={pts.B.x} y2={pts.B.y} stroke={line} strokeWidth="1.8" />
        <line x1={pts.S.x} y1={pts.S.y} x2={pts.C.x} y2={pts.C.y} stroke={line} strokeWidth="1.8" />
        <line x1={pts.S.x} y1={pts.S.y} x2={pts.D.x} y2={pts.D.y} stroke={line} strokeWidth="1.8" strokeDasharray="5 5" />
        <line x1={pts.S.x} y1={pts.S.y} x2={pts.M.x} y2={pts.M.y} stroke={accent} strokeWidth="2.2" />
        <path
          d={`M ${pts.M.x - 17} ${pts.M.y - 1} C ${pts.M.x - 17} ${pts.M.y - 9}, ${pts.M.x - 11} ${pts.M.y - 16}, ${pts.M.x + 1} ${pts.M.y - 18}`}
          fill="none"
          stroke={accent}
          strokeWidth="1.5"
        />
        <circle cx={pts.M.x - 6} cy={pts.M.y - 6} r="2.1" fill={accent} />
        <g transform={`rotate(${heightAngle + 180} ${heightLabel.x} ${heightLabel.y})`}>
          <SvgMathLabel
            x={heightLabel.x}
            y={heightLabel.y}
            math={`${faceHeight}\\text{ cm}`}
            fontSize={13}
            color={accent}
          />
        </g>
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>
        Dany jest ostrosłup prawidłowy czworokątny, w którym wysokość ściany
        bocznej poprowadzona do krawędzi podstawy jest równa{" "}
        <InlineMath math={`${data.faceHeight}\\text{ cm}`} />.
      </p>
      <PyramidSketch faceHeight={data.faceHeight} />
      <p>
        Pole powierzchni jednej ściany bocznej tego ostrosłupa jest równe{" "}
        <InlineMath math={`${data.lateralFaceArea}\\text{ cm}^2`} />.
      </p>
      <p>
        <strong>Oblicz sumę długości wszystkich krawędzi tego ostrosłupa. Zapisz obliczenia.</strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left", marginTop: 10 }}>
      <div
        style={{
          fontSize: 12.5,
          opacity: 0.78,
          lineHeight: 1.45,
          borderLeft: "3px solid var(--color-accent)",
          paddingLeft: 8,
        }}
      >
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz dosta\u0107 punkty cz\u0105stkowe. Poprawna suma d\u0142ugo\u015bci wszystkich kraw\u0119dzi z jednostk\u0105 daje pe\u0142ne 3 punkty."}
      </div>
      <NumberInputRow label="Długość krawędzi podstawy:" value={userAnswer.baseEdge} onChange={setField("baseEdge")} disabled={disabled} />
      <NumberInputRow label="Długość krawędzi bocznej:" value={userAnswer.lateralEdge} onChange={setField("lateralEdge")} disabled={disabled} />
      <NumberInputRow label="Suma długości wszystkich krawędzi:" value={userAnswer.edgeSum} onChange={setField("edgeSum")} disabled={disabled} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const parts = {
    "krawędź podstawy": isNumCorrect(userAnswer.baseEdge, data.baseEdge),
    "krawędź boczna": isNumCorrect(userAnswer.lateralEdge, data.lateralEdge),
    "suma krawędzi": isNumCorrect(userAnswer.edgeSum, data.edgeSum),
  };
  return scoreStaged({
    resultOk: parts["suma krawędzi"],
    milestones: [parts["krawędź podstawy"], parts["krawędź boczna"]],
    maxScore: data.maxPoints,
    parts,
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${data.lateralFaceArea}=\\frac{a\\cdot ${data.faceHeight}}{2}`} />
      <BlockMath math={`a=\\frac{2\\cdot ${data.lateralFaceArea}}{${data.faceHeight}}=${data.baseEdge}\\text{ cm}`} />
      <BlockMath math={`k_b^2=${data.halfBase}^2+${data.faceHeight}^2=${data.lateralEdge ** 2}`} />
      <BlockMath math={`k_b=${data.lateralEdge}\\text{ cm}`} />
      <BlockMath math={`4\\cdot ${data.baseEdge}+4\\cdot ${data.lateralEdge}=${data.edgeSum}\\text{ cm}`} />
      <div>{"Odpowied\u017a: suma d\u0142ugo\u015bci kraw\u0119dzi jest r\u00f3wna "}<InlineMath math={`${data.edgeSum}\\text{ cm}`} />{"."}</div>
    </div>
  );
}

function answerLatex(value) {
  const parsed = parseNum(value);
  return parsed == null ? "\\text{brak}" : `${parsed}\\text{ cm}`;
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div><InlineMath math={`a=${answerLatex(userAnswer.baseEdge)}`} /></div>
      <div><InlineMath math={`k_b=${answerLatex(userAnswer.lateralEdge)}`} /></div>
      <div><InlineMath math={`S=${answerLatex(userAnswer.edgeSum)}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Jedna ściana boczna jest trójkątem o podstawie
        równej krawędzi podstawy ostrosłupa. Korzystamy ze wzoru na pole trójkąta.
      </p>
      <BlockMath math={`108=\\frac{a\\cdot ${data.faceHeight}}{2}`} />
      <BlockMath math={`a=\\frac{2\\cdot ${data.lateralFaceArea}}{${data.faceHeight}}=${data.baseEdge}\\text{ cm}`} />
      <p>
        <strong>Krok 2.</strong> W ścianie bocznej wysokość dzieli podstawę na
        połowy. Powstaje trójkąt prostokątny o przyprostokątnych{" "}
        <InlineMath math={`${data.halfBase}`} /> i <InlineMath math={`${data.faceHeight}`} />.
      </p>
      <BlockMath math={`k_b^2=${data.halfBase}^2+${data.faceHeight}^2=${data.lateralEdge ** 2}`} />
      <BlockMath math={`k_b=${data.lateralEdge}\\text{ cm}`} />
      <p>
        <strong>Krok 3.</strong> Ostrosłup prawidłowy czworokątny ma cztery
        krawędzie podstawy i cztery krawędzie boczne.
      </p>
      <BlockMath math={`4\\cdot ${data.baseEdge}+4\\cdot ${data.lateralEdge}=${data.edgeSum}\\text{ cm}`} />
    </div>
  );
}
